import type { ReactNode } from "react";
import { StyleSheet, Text } from "react-native";

import { fonts, useThemedStyles, type ThemeColors } from "~/styles/theme";
import type { TextSize, TextWeight } from "./types";

export function AppText({
  children,
  size = "sm",
  weight,
  tone = "default",
  selectable,
}: {
  children: ReactNode;
  size?: TextSize;
  weight?: TextWeight;
  tone?: "default" | "muted" | "danger" | "inverse" | "link";
  selectable?: boolean;
}) {
  const { stylesheet, sizeStyles, weightStyles } = useThemedStyles(makeStyles);
  return (
    <Text
      selectable={selectable}
      style={[
        sizeStyles[size],
        weight ? weightStyles[weight] : null,
        tone === "muted" ? stylesheet.muted : null,
        tone === "danger" ? stylesheet.danger : null,
        tone === "inverse" ? stylesheet.inverse : null,
        tone === "link" ? stylesheet.link : null,
      ]}
    >
      {children}
    </Text>
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    xs: {
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 12,
      lineHeight: 17,
    },
    sm: {
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 14,
      lineHeight: 22,
    },
    base: {
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 16,
      lineHeight: 26,
    },
    lg: {
      color: colors.foreground,
      fontFamily: fonts.regular,
      fontSize: 18,
      lineHeight: 28,
    },
    medium: { fontFamily: fonts.medium },
    semibold: { fontFamily: fonts.semibold },
    bold: { fontFamily: fonts.bold },
    muted: { color: colors.mutedForeground },
    danger: { color: colors.danger },
    inverse: { color: colors.primaryForeground },
    link: { color: colors.primary },
  });
  return {
    stylesheet,
    sizeStyles: {
      xs: stylesheet.xs,
      sm: stylesheet.sm,
      base: stylesheet.base,
      lg: stylesheet.lg,
    },
    weightStyles: {
      medium: stylesheet.medium,
      semibold: stylesheet.semibold,
      bold: stylesheet.bold,
    },
  };
};
